import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AdminSection } from "./admin";
import {
  AdminButton, AdminDialog, Field, Input, Row, StatusBadge, TableShell, Textarea,
} from "@/components/admin/AdminUI";

export const Route = createFileRoute("/_authenticated/admin/app-releases")({
  component: AppReleasesAdmin,
});

interface Release {
  id: string;
  platform: string;
  channel: string;
  version_name: string;
  version_code: number;
  min_os_version: string | null;
  download_url: string;
  file_size_bytes: number | null;
  sha256: string | null;
  signing_fingerprint: string | null;
  release_notes: string | null;
  mandatory: boolean;
  active: boolean;
  published_at: string;
}

const EMPTY = {
  platform: "android" as "android" | "ios" | "desktop" | "pwa",
  channel: "stable" as "stable" | "beta" | "critical",
  version_name: "",
  version_code: "",
  min_os_version: "8.0",
  download_url: "",
  file_size_bytes: "",
  sha256: "",
  signing_fingerprint: "",
  release_notes: "",
  mandatory: false,
  active: false,
};

function AppReleasesAdmin() {
  const [rows, setRows] = useState<Release[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Release | "new" | null>(null);

  async function load() {
    setLoading(true);
    const { data } = await supabase
      .from("app_releases")
      .select("*")
      .order("platform")
      .order("version_code", { ascending: false });
    setRows((data ?? []) as Release[]);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function toggleActive(r: Release) {
    // If activating, deactivate other rows on same platform+channel first.
    if (!r.active) {
      await supabase
        .from("app_releases")
        .update({ active: false })
        .eq("platform", r.platform)
        .eq("channel", r.channel);
    }
    await supabase.from("app_releases").update({ active: !r.active }).eq("id", r.id);
    load();
  }

  async function remove(id: string) {
    if (!confirm("Delete this release? The APK file in storage is kept.")) return;
    const { error } = await supabase.from("app_releases").delete().eq("id", id);
    if (error) alert(error.message); else load();
  }

  return (
    <AdminSection
      title="App Release Center"
      description="Sovereign, first-party distribution. Publish signed Android APKs, PWA versions, and desktop builds directly from buddhaka.com."
      action={<AdminButton onClick={() => setEditing("new")}>+ New release</AdminButton>}
    >
      <div className="mb-6 rounded-lg border border-gold/40 bg-gold/5 p-4 text-xs leading-relaxed text-muted-foreground">
        <p className="font-serif text-sm text-gold-luminous">Release workflow</p>
        <ol className="mt-2 list-decimal space-y-1 pl-4">
          <li>Build the signed APK locally with your permanent keystore (see <code>docs/sovereign/android-signing.md</code>).</li>
          <li>Upload it here — the SHA-256 is computed in your browser and stored alongside the manifest.</li>
          <li>Set the release to <em>Active</em> to make it live at <code>/api/public/releases/android/download</code>.</li>
          <li>Only one release per platform+channel can be active at a time; activating a new one deactivates the previous.</li>
        </ol>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No releases published yet.</p>
      ) : (
        <TableShell
          headers={["Platform", "Version", "Channel", "Size", "Published", "Status", ""]}
          rows={rows.map((r) => (
            <tr key={r.id} className="hover:bg-obsidian/40">
              <td className="px-4 py-3 text-muted-foreground uppercase text-xs tracking-widest">{r.platform}</td>
              <td className="px-4 py-3">
                <div className="font-serif text-base text-gold-luminous">v{r.version_name}</div>
                <div className="text-xs text-muted-foreground">code {r.version_code}</div>
              </td>
              <td className="px-4 py-3 text-muted-foreground">{r.channel}{r.mandatory ? " · critical" : ""}</td>
              <td className="px-4 py-3 text-muted-foreground">{r.file_size_bytes ? `${(r.file_size_bytes / 1024 / 1024).toFixed(1)} MB` : "—"}</td>
              <td className="px-4 py-3 text-muted-foreground">{new Date(r.published_at).toLocaleDateString()}</td>
              <td className="px-4 py-3">
                <StatusBadge status={r.active ? "live" : "draft"} />
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-2">
                  <AdminButton variant="ghost" onClick={() => toggleActive(r)}>{r.active ? "Deactivate" : "Activate"}</AdminButton>
                  <AdminButton variant="ghost" onClick={() => setEditing(r)}>Edit</AdminButton>
                  <AdminButton variant="danger" onClick={() => remove(r.id)}>Delete</AdminButton>
                </div>
              </td>
            </tr>
          ))}
        />
      )}

      {editing && (
        <ReleaseForm
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </AdminSection>
  );
}

async function sha256Hex(buffer: ArrayBuffer): Promise<string> {
  const hash = await crypto.subtle.digest("SHA-256", buffer);
  return Array.from(new Uint8Array(hash)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

function ReleaseForm({ initial, onClose, onSaved }: { initial: Release | null; onClose: () => void; onSaved: () => void }) {
  const isNew = !initial;
  const [f, setF] = useState(initial ? {
    platform: initial.platform as typeof EMPTY.platform,
    channel: initial.channel as typeof EMPTY.channel,
    version_name: initial.version_name,
    version_code: String(initial.version_code),
    min_os_version: initial.min_os_version ?? "",
    download_url: initial.download_url,
    file_size_bytes: initial.file_size_bytes?.toString() ?? "",
    sha256: initial.sha256 ?? "",
    signing_fingerprint: initial.signing_fingerprint ?? "",
    release_notes: initial.release_notes ?? "",
    mandatory: initial.mandatory,
    active: initial.active,
  } : EMPTY);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setError(null); setProgress("Reading file…");
    try {
      const buf = await file.arrayBuffer();
      setProgress("Computing SHA-256…");
      const sha = await sha256Hex(buf);
      setProgress("Uploading to secure storage…");
      const versionPath = f.version_name || file.name.replace(/\.apk$/i, "");
      const path = `${f.platform}/kafrequency-${versionPath}-${Date.now()}.apk`;
      const { error: upErr } = await supabase.storage
        .from("app-releases")
        .upload(path, file, { contentType: "application/vnd.android.package-archive", upsert: false });
      if (upErr) throw upErr;
      setF((prev) => ({
        ...prev,
        download_url: path,
        file_size_bytes: String(file.size),
        sha256: sha,
      }));
      setProgress("Upload complete.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
      setProgress(null);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    const user = (await supabase.auth.getUser()).data.user;
    const payload = {
      platform: f.platform,
      channel: f.channel,
      version_name: f.version_name.trim(),
      version_code: parseInt(f.version_code, 10),
      min_os_version: f.min_os_version.trim() || null,
      download_url: f.download_url.trim(),
      file_size_bytes: f.file_size_bytes ? parseInt(f.file_size_bytes, 10) : null,
      sha256: f.sha256.trim() || null,
      signing_fingerprint: f.signing_fingerprint.trim() || null,
      release_notes: f.release_notes,
      mandatory: f.mandatory,
      active: f.active,
      published_by: user?.id ?? null,
    };
    if (!payload.version_name || !payload.version_code || !payload.download_url) {
      setBusy(false); setError("Version name, version code, and download path are required."); return;
    }
    if (f.active && isNew) {
      // Deactivate previous active on same platform+channel
      await supabase.from("app_releases").update({ active: false })
        .eq("platform", payload.platform).eq("channel", payload.channel);
    }
    const q = isNew
      ? supabase.from("app_releases").insert(payload)
      : supabase.from("app_releases").update(payload).eq("id", initial!.id);
    const { error } = await q;
    setBusy(false);
    if (error) setError(error.message); else onSaved();
  }

  return (
    <AdminDialog open onClose={onClose} title={isNew ? "New release" : `Edit · v${initial?.version_name}`}>
      <form onSubmit={submit} className="space-y-4">
        <Row>
          <Field label="Platform">
            <select value={f.platform} onChange={(e) => setF({ ...f, platform: e.target.value as typeof EMPTY.platform })} className="w-full rounded-md border hairline bg-transparent px-3 py-2 text-sm">
              <option value="android" className="bg-background">Android (APK)</option>
              <option value="pwa" className="bg-background">PWA</option>
              <option value="desktop" className="bg-background">Desktop</option>
              <option value="ios" className="bg-background">iOS</option>
            </select>
          </Field>
          <Field label="Channel">
            <select value={f.channel} onChange={(e) => setF({ ...f, channel: e.target.value as typeof EMPTY.channel })} className="w-full rounded-md border hairline bg-transparent px-3 py-2 text-sm">
              <option value="stable" className="bg-background">stable</option>
              <option value="beta" className="bg-background">beta</option>
              <option value="critical" className="bg-background">critical</option>
            </select>
          </Field>
        </Row>
        <Row>
          <Field label="Version name" hint="e.g. 1.0.0"><Input required value={f.version_name} onChange={(e) => setF({ ...f, version_name: e.target.value })} /></Field>
          <Field label="Version code" hint="integer, must increase each release"><Input required type="number" value={f.version_code} onChange={(e) => setF({ ...f, version_code: e.target.value })} /></Field>
        </Row>
        <Row>
          <Field label="Minimum OS version" hint="e.g. Android 8.0"><Input value={f.min_os_version} onChange={(e) => setF({ ...f, min_os_version: e.target.value })} /></Field>
          <Field label="File size (bytes)"><Input type="number" value={f.file_size_bytes} onChange={(e) => setF({ ...f, file_size_bytes: e.target.value })} /></Field>
        </Row>

        {f.platform === "android" && (
          <div className="rounded-xl border hairline bg-obsidian/40 p-4">
            <p className="text-xs uppercase tracking-widest text-gold">Upload signed APK</p>
            <p className="mt-1 text-[11px] text-muted-foreground">Reads the file locally, computes SHA-256, uploads to private storage, and fills in the fields below.</p>
            <input
              ref={fileRef}
              type="file"
              accept=".apk,application/vnd.android.package-archive"
              onChange={onFile}
              disabled={uploading}
              className="mt-3 block w-full text-xs text-muted-foreground file:mr-3 file:rounded-md file:border-0 file:bg-gold file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-obsidian hover:file:bg-gold-luminous"
            />
            {progress && <p className="mt-2 text-xs text-gold">{progress}</p>}
          </div>
        )}

        <Field label="Download path (storage key)" hint="Auto-filled by upload; edit only if the file exists at a specific path"><Input required value={f.download_url} onChange={(e) => setF({ ...f, download_url: e.target.value })} /></Field>
        <Field label="SHA-256 checksum"><Input value={f.sha256} onChange={(e) => setF({ ...f, sha256: e.target.value })} placeholder="hex, 64 chars" /></Field>
        <Field label="Signing certificate fingerprint (SHA-256)"><Input value={f.signing_fingerprint} onChange={(e) => setF({ ...f, signing_fingerprint: e.target.value })} placeholder="AA:BB:CC:…" /></Field>
        <Field label="Release notes"><Textarea rows={5} value={f.release_notes} onChange={(e) => setF({ ...f, release_notes: e.target.value })} /></Field>

        <div className="flex flex-wrap gap-4 border-t hairline pt-4">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} />
            <span>Active (public download)</span>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.mandatory} onChange={(e) => setF({ ...f, mandatory: e.target.checked })} />
            <span>Mandatory update</span>
          </label>
        </div>

        {error && <p className="text-sm text-red-400">{error}</p>}
        <div className="flex justify-end gap-2 border-t hairline pt-4">
          <AdminButton type="button" variant="ghost" onClick={onClose}>Cancel</AdminButton>
          <AdminButton type="submit" disabled={busy || uploading}>{busy ? "Saving…" : isNew ? "Publish" : "Save"}</AdminButton>
        </div>
      </form>
    </AdminDialog>
  );
}
